/**
 * 檔案用途區塊
 * @module AppKitProviderBootstrap
 * @purpose 讀取 URL 參數中的 tokens 與 clientId，並將結果注入 AppKitProvider 以完成啟動階段設定。
 * @external https://innotech.atlassian.net/browse/IN-111892
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
import React, { useMemo } from 'react';
import { isAppKitEnabled } from '@shared/helpers/appkit-enabled.helper';
import AppKitProvider from './index';

interface AppKitProviderBootstrapProps {
  children: React.ReactNode;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description App 層級的 Provider 啟動包裝元件。
 * @purpose 在渲染 AppKitProvider 前先完成 URL 參數解析，確保初始化輸入已固定。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
const AppKitProviderBootstrap: React.FC<AppKitProviderBootstrapProps> = ({ children }) => {
  /**
   * 宣告內容用途說明與單號關聯
   * @description 以 memo 方式於首次渲染解析 tokens 與 clientId。
   * @purpose 避免重複解析 query string，並提供穩定的 Provider 初始化參數。
   * @external https://innotech.atlassian.net/browse/IN-113493
   */
  const { tokens, clientId } = useMemo((): {
    tokens: string[];
    clientId: string;
  } => {
    /**
     * 宣告內容用途說明與單號關聯
     * @description 建立 URLSearchParams 以讀取目前頁面的查詢參數。
     * @purpose 作為 tokens 與 clientId 解析來源。
     * @external https://innotech.atlassian.net/browse/IN-111892
     */
    const urlParams = new URLSearchParams(window.location.search);
    /**
     * 宣告內容用途說明與單號關聯
     * @description 取得原始 tokens 參數字串。
     * @purpose 作為後續切割與清理 token 清單的輸入。
     * @external https://innotech.atlassian.net/browse/IN-111892
     */
    const tokensParam = urlParams.get('tokens');
    /**
     * 宣告內容用途說明與單號關聯
     * @description 將逗號分隔 tokens 字串轉為乾淨的 token 陣列。
     * @purpose 過濾空白與空值，提供 AppKitProvider 可直接使用的 tokens。
     * @external https://innotech.atlassian.net/browse/IN-111892
     */
    const parsedTokens: string[] = tokensParam
      ? tokensParam
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0)
      : [];

    /**
     * 宣告內容用途說明與單號關聯
     * @description 取得 clientId，若缺失則以空字串表示。
     * @purpose 統一回傳型別並交由下游 Provider 決定後續處理。
     * @external https://innotech.atlassian.net/browse/IN-111892
     */
    const clientIdParam = urlParams.get('clientId') || '';

    return {
      tokens: parsedTokens,
      clientId: clientIdParam,
    };
  }, []);

  /**
   * 宣告內容用途說明與單號關聯
   * @description 非 WalletConnect 路由或 DISABLE_APPKIT 時略過 AppKit 初始化。
   * @purpose 封裝啟用條件，避免誤用時載入 wagmi/AppKit 或在非預期路由噴錯。
   * @external https://innotech.atlassian.net/browse/IN-132161
   */
  if (!isAppKitEnabled()) {
    return <>{children}</>;
  }

  return (
    <AppKitProvider tokens={tokens} clientId={clientId}>
      {children}
    </AppKitProvider>
  );
};

export default AppKitProviderBootstrap;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:20:01.401Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Rewrote annotations into the required three-section layout, added top/middle/bottom JSDoc blocks with mandated tags, attached declaration-level Jira URLs strictly from declarationOrigins, removed old free-form comments, and kept all runtime logic unchanged.
 */
